import React, {useState} from 'react';

import {FolderSummary} from 'constants/FolderConstants';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SearchContext} from 'search/SearchConstants';
import ItemSearchInput from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import {ElementKeyboardEvent} from 'util/Types';

import {
  doProcedureSearch,
  procedureSearchUrl,
  procedureSearchFilters,
  procedureSearchSorts,
} from './ProcedureSearch';

/**
 * These fields are needed by some callers for resolving a search response into a ProceduresTypedValue.
 */
export interface ProcedureSearchItem {
  id: string;
  name: string;
  isReadOnly: boolean;
}

export type ProcedureSearchItemKeys = keyof ProcedureSearchItem;

/**
 * Implicit contract with `/1/api/all/search` response that we return id, name, folder.
 */
export interface ProcedureSearchResponse {
  // The fields in ProcedureSearchItem as well as folder are needed for ItemOptionContent.
  data: Array<ProcedureSearchItem & {folder: FolderSummary}>;
}

export interface ProcedureSearchInputProps<TProcedureSearchItem = ProcedureSearchItem> {
  procedureTypes?: Array<ProcedureType>;
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  initialQuery?: string;
  onBlur?: () => void;
  onChange: (Procedures: Array<TProcedureSearchItem>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  selectedItems: Array<TProcedureSearchItem>;
  shouldAllowMultiple?: boolean;
  placeholder?: string;
  context?: SearchContext;
  /** Specifies whether input is editable without changing UI */
  disabled?: boolean;
  /** Specifies whether the input outline renders and if is editable */
  isReadOnly?: boolean;
  /** Specifies whether chips are interactive */
  hasNonInteractiveChips?: boolean;
  hasVerticalChips?: boolean;

  // This component can optionally be controlled.
  query?: string;
  onUpdateQuery?: (query: string) => void;
}

/**
 * Input to search and select one or more Procedures.
 */
export default function ProcedureSearchInput<K extends ProcedureSearchItemKeys = ProcedureSearchItemKeys>(
  props: ProcedureSearchInputProps<Pick<ProcedureSearchItem, 'id' | K>>
): JSX.Element {
  const searchFetchParams = {
    context: props.context,
    groupBy: null,
    limit: 10,
    nextToken: null,
    offset: 0,
    ...{
      filters: procedureSearchFilters(props.procedureTypes),
      sorts: procedureSearchSorts,
    },
  };
  return props.isReadOnly ? (
    <ChipList
      chipSize={ChipSize.SMALL}
      showMoreSize={ChipSize.SMALL}
      isVertical={props.hasVerticalChips ?? false}
      itemIds={props.selectedItems.map((item) => item.id)}
      maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
    />
  ) : (
    <ItemSearchInput<Pick<ProcedureSearchItem, 'id' | K>, ProcedureSearchResponse>
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      autoFocus={props.autoFocus ?? false}
      chipSize={props.chipSize}
      className={props.className}
      doSearch={doProcedureSearch}
      fetchParams={searchFetchParams}
      initialQuery={props.initialQuery}
      itemKey="data"
      onBlur={props.onBlur}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      placeholder={props.placeholder}
      searchURL={procedureSearchUrl}
      selectedItems={props.selectedItems}
      shouldAllowMultiple={props.shouldAllowMultiple}
      hideSelectedResults={true}
      disabled={props.disabled}
    />
  );
}

export type ProcedureSearchControlledItemsProps = Pick<
  ProcedureSearchInputProps,
  'onChange' | 'selectedItems'
>;

export function useProcedureSearchControlledItemsProps(
  initialValue: Array<ProcedureSearchItem>
): ProcedureSearchControlledItemsProps {
  const [procedureSearchItems, setProcedureSearchItems] = useState<Array<ProcedureSearchItem>>(initialValue);
  return {selectedItems: procedureSearchItems, onChange: setProcedureSearchItems};
}

export function getProcedureIds(
  ProcedureSearchControlledItemsProps?: ProcedureSearchControlledItemsProps
): Array<string> {
  if (!ProcedureSearchControlledItemsProps) {
    return [];
  }
  return ProcedureSearchControlledItemsProps?.selectedItems?.map((selectedItem) => selectedItem.id);
}
